import { useMemo, useState, useCallback, useEffect } from "react"
import { toast } from "react-toastify"
import { Col, Row, UncontrolledTooltip, Button } from "reactstrap"

// Custom Components
import TableContainer from "components/Common/TableContainer"
import DeleteModal from "components/Common/DeleteModal"
import GlobalInputSearch from "components/Common/GlobalInputSearch"
import ToggleButton from "components/Common/ToggleButton"
import { CanView } from "components/Common/CanView"

// API & Permissions
import { downloadAndOpenFile } from "api/pdfApi"
import { ACCESS } from "constants/accessPermissions"

// Hooks
import {
  useVehicleMovements,
  useDeleteVehicleMovement,
} from "hooks/useVehicleMovements"
import { useListParams } from "hooks/useListParams"

// Utils
import { formatReadableDate } from "utils/formatDate"
import { useFilteredVehicleOptions } from "hooks/stockFilterHook"
import StockModelCell from "components/Common/stock-table/StockModelCell"
import MovementInfoCell from "components/Common/VehicleMovement/MovementInfoCell"
import VehicleMovementFilter from "./VehicleMovementFilter"
import { VehicleMovementListHeader } from "./VehicleMovementListHeader"
import VehicleMovementAddEdit from "./VehicleMovementAddEdit"
import { useResetParamsOnTabChange } from "hooks/useResetParamsOnTabChange"

const isCustomerDeliveryMovement = movement => {
  const movementType = movement && movement.movement_type
  const slug = movementType && movementType.slug
  const name = movementType && movementType.name

  return slug === "customer-delivery" || name === "Customer Delivery"
}

const VehicleMovementList = ({
  vehicleId = null,
  vehicle = null,
  customerId = null,
  customer = null,
}) => {
  // Scoped mode: embedded inside a stock view (vehicle) or customer profile (customer)
  const scoped = !!vehicleId || !!customerId
  const listTabId = vehicleId
    ? "vehicle-movement"
    : customerId
      ? "customer-movement"
      : "movement-list"

  if (!scoped) {
    document.title = "Vehicle Movement List"
  }

  // reset url params except 'tab', 'page'
  useResetParamsOnTabChange(listTabId)

  // State
  const [modal, setModal] = useState(false)
  const [isEdit, setIsEdit] = useState(false)
  const [movement, setMovement] = useState(null)
  const [deleteModal, setDeleteModal] = useState(false)
  const [printingId, setPrintingId] = useState(null)

  // URL search params hook
  const {
    params,
    setPage,
    setSearch,
    resetParams,
    updateParams,
    hasActiveFilters,
  } = useListParams({
    page: 1,
    search: "",
    movement_type_id: "",
    location_to_id: "",
    movement_by_id: "",
    date_from: "",
    date_to: "",
  })

  // Query params
  const queryParams = useMemo(() => {
    const {
      page = 1,
      per_page = 10,
      search,
      movement_type_id,
      location_to_id,
      movement_by_id,
      date_from,
      date_to,
    } = params

    const q = { page, per_page }

    q.search = search
    q.movement_type_id = movement_type_id
    q.location_to_id = location_to_id
    q.movement_by_id = movement_by_id
    q.date_from = date_from
    q.date_to = date_to

    if (vehicleId) {
      q.vehicle_id = vehicleId
    }

    if (customerId) {
      q.customer_id = customerId
    }

    return q
  }, [
    params.page,
    params.search,
    params.movement_type_id,
    params.location_to_id,
    params.movement_by_id,
    params.date_from,
    params.date_to,
    vehicleId,
    customerId,
  ])

  // React Query hooks
  const {
    data: movementsResponse,
    isLoading,
    refetch: refetchMovements,
    isRefetching,
  } = useVehicleMovements(queryParams, params?.tab === listTabId)

  const { mutateAsync: deleteMovement, isPending: deleteLoading } =
    useDeleteVehicleMovement()

  // Options hook
  const { isLoading: optionLoading, raw: rawData } = useFilteredVehicleOptions()

  // Memoize the data to prevent unnecessary re-renders
  const movementTypesData = useMemo(
    () => rawData?.movement_types ?? [],
    [rawData?.movement_types]
  )

  const locationsData = useMemo(
    () => rawData?.locations ?? [],
    [rawData?.locations]
  )

  const employeesData = useMemo(
    () => rawData?.employees ?? [],
    [rawData?.employees]
  )

  // Toggle modal
  const toggleModal = useCallback(() => {
    if (modal) {
      setModal(false)
      setMovement(null)
      setIsEdit(false)
    } else setModal(true)
  }, [modal])

  const handleAddNewClick = useCallback(() => {
    setMovement(null)
    setIsEdit(false)
    toggleModal()
  }, [toggleModal])

  const handleEditClick = useCallback(
    item => {
      setMovement(item)
      setIsEdit(true)
      toggleModal()
    },
    [toggleModal]
  )

  const handleDeleteClick = useCallback(item => {
    setMovement(item)
    setDeleteModal(true)
  }, [])

  const handleDelete = useCallback(async () => {
    if (!movement?.id) return
    try {
      const res = await deleteMovement(movement.id)
      toast.success(res?.message ?? "Vehicle movement deleted successfully.")
      setDeleteModal(false)
    } catch (error) {
      console.error(error)
      toast.error("Failed to delete vehicle movement.")
    }
  }, [movement, deleteMovement])

  const handleRefresh = () => refetchMovements()

  // Open the vehicle movement pass (delivery challan) PDF in a new tab
  const handleMovementPassPrint = useCallback(async item => {
    if (!item?.id) return
    try {
      setPrintingId(item.id)
      await downloadAndOpenFile({
        url: `/vehicle-movements/${item.id}/movement-pass`,
      })
    } catch (error) {
      console.error(error)
      toast.error("Failed to generate movement pass.")
    } finally {
      setPrintingId(null)
    }
  }, [])

  // Open the vehicle handover form PDF in a new tab
  const handleHandoverPrint = useCallback(async item => {
    if (!item?.id) return
    try {
      setPrintingId(item.id)
      await downloadAndOpenFile({
        url: `/vehicle-movements/${item.id}/handover`,
      })
    } catch (error) {
      console.error(error)
      toast.error("Failed to generate handover form.")
    } finally {
      setPrintingId(null)
    }
  }, [])

  // Open the BRTA inspection request letter PDF in a new tab
  const handleBrtaInspectionPrint = useCallback(async item => {
    if (!item?.id) return
    try {
      setPrintingId(item.id)
      await downloadAndOpenFile({
        url: `/vehicle-movements/${item.id}/brta-inspection`,
      })
    } catch (error) {
      console.error(error)
      toast.error("Failed to generate BRTA inspection letter.")
    } finally {
      setPrintingId(null)
    }
  }, [])

  // Table columns
  const columns = useMemo(
    () => [
      { header: "ID", accessorKey: "id" },
      {
        header: "Vehicle Info",
        accessorKey: "vehicle_info",
        cell: ({ row }) => (
          <StockModelCell
            stock={row.original?.vehicle ?? {}}
            isMobile={false}
            isExpanded={() => false}
            toggleRow={() => {}}
            rowId={row.original?.id}
            showIdentifiers
            showImage
          />
        ),
      },
      {
        header: "Movement Info",
        accessorKey: "movement_info",
        cell: ({ row }) => <MovementInfoCell data={row.original} />,
      },
      {
        header: "Remarks",
        accessorKey: "remarks",
        cell: ({ row }) => row.original.remarks || "-",
      },
      {
        header: "Created",
        accessorKey: "created_at",
        cell: ({ row }) =>
          row.original.created_at
            ? formatReadableDate(row.original.created_at)
            : "-",
      },
      {
        header: "Action",
        cell: ({ row }) => {
          const movement = row.original
          const isLocked = isCustomerDeliveryMovement(movement)

          return (
            <ul className="list-unstyled hstack gap-1 mb-0">
              <CanView access={[ACCESS.VEHICLE_MOVEMENT.MOVEMENT_PASS_PDF]}>
                <li>
                  <Button
                    color="secondary"
                    size="sm"
                    onClick={() => handleMovementPassPrint(movement)}
                    disabled={printingId === movement.id}
                    id={`passtooltip-${movement.id}`}
                  >
                    <i
                      className={`mdi ${
                        printingId === movement.id
                          ? "mdi-progress-download"
                          : "mdi-download"
                      }`}
                    />
                    <UncontrolledTooltip
                      placement="top"
                      target={`passtooltip-${movement.id}`}
                    >
                      Movement Pass
                    </UncontrolledTooltip>
                  </Button>
                </li>
              </CanView>
              {movement.handover && (
                <CanView access={[ACCESS.VEHICLE_MOVEMENT.HANDOVER_PDF]}>
                  <li>
                    <Button
                      color="primary"
                      size="sm"
                      onClick={() => handleHandoverPrint(movement)}
                      disabled={printingId === movement.id}
                      id={`handovertooltip-${movement.id}`}
                    >
                      <i
                        className={`mdi ${
                          printingId === movement.id
                            ? "mdi-progress-download"
                            : "mdi-file-document-outline"
                        }`}
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target={`handovertooltip-${movement.id}`}
                      >
                        Hand Over
                      </UncontrolledTooltip>
                    </Button>
                  </li>
                </CanView>
              )}
              {movement.brta_inspection && (
                <CanView access={[ACCESS.VEHICLE_MOVEMENT.BRTA_INSPECTION_PDF]}>
                  <li>
                    <Button
                      color="success"
                      size="sm"
                      onClick={() => handleBrtaInspectionPrint(movement)}
                      disabled={printingId === movement.id}
                      id={`brtatooltip-${movement.id}`}
                    >
                      <i
                        className={`mdi ${
                          printingId === movement.id
                            ? "mdi-progress-download"
                            : "mdi-file-certificate-outline"
                        }`}
                      />
                      <UncontrolledTooltip
                        placement="top"
                        target={`brtatooltip-${movement.id}`}
                      >
                        BRTA Inspection
                      </UncontrolledTooltip>
                    </Button>
                  </li>
                </CanView>
              )}
              {isLocked ? (
                <li>
                  <Button
                    color="light"
                    size="sm"
                    disabled
                    id={`lockedtooltip-${movement.id}`}
                  >
                    <i className="mdi mdi-lock-outline" />
                    <UncontrolledTooltip
                      placement="top"
                      target={`lockedtooltip-${movement.id}`}
                    >
                      Customer Delivery is locked
                    </UncontrolledTooltip>
                  </Button>
                </li>
              ) : (
                <>
                  <li>
                    <Button
                      color="info"
                      size="sm"
                      onClick={() => handleEditClick(movement)}
                      id={`edittooltip-${movement.id}`}
                    >
                      <i className="mdi mdi-pencil-outline" />
                      <UncontrolledTooltip
                        placement="top"
                        target={`edittooltip-${movement.id}`}
                      >
                        Edit
                      </UncontrolledTooltip>
                    </Button>
                  </li>
                  <li>
                    <Button
                      color="danger"
                      size="sm"
                      onClick={() => handleDeleteClick(movement)}
                      id={`deletetooltip-${movement.id}`}
                    >
                      <i className="mdi mdi-delete-outline" />
                      <UncontrolledTooltip
                        placement="top"
                        target={`deletetooltip-${movement.id}`}
                      >
                        Delete
                      </UncontrolledTooltip>
                    </Button>
                  </li>
                </>
              )}
            </ul>
          )
        },
      },
    ],
    [
      handleEditClick,
      handleDeleteClick,
      handleMovementPassPrint,
      handleHandoverPrint,
      handleBrtaInspectionPrint,
      printingId,
    ]
  )

  const data = movementsResponse?.data?.data || []
  const paginationInfo = movementsResponse?.data

  return (
    <>
      <VehicleMovementListHeader
        handleAddNewClick={handleAddNewClick}
        handleRefresh={handleRefresh}
        resetParams={resetParams}
        isRefetching={isRefetching}
        hasActiveFilters={hasActiveFilters}
        total={paginationInfo?.total ?? 0}
      />

      <div className="pb-3"></div>

      <DeleteModal
        show={deleteModal}
        onDeleteClick={handleDelete}
        loading={deleteLoading}
        onCloseClick={() => setDeleteModal(false)}
      />

      <div className="mb-3">
        <ToggleButton initialOpen={false}>
          <VehicleMovementFilter
            compact={scoped}
            filters={params}
            setFilters={updateParams}
            movementTypes={movementTypesData?.map(item => ({
              label: item?.name,
              value: item?.id,
            }))}
            movementByOptions={employeesData?.map(item => ({
              label: item?.name,
              value: item?.id,
            }))}
            locationOptions={locationsData?.map(item => ({
              label: item?.name,
              value: item?.id,
            }))}
            loading={optionLoading}
          />
        </ToggleButton>
      </div>

      <Row>
        <Col sm={4}>
          <GlobalInputSearch value={params.search} onChange={setSearch} />
        </Col>
      </Row>
      <TableContainer
        columns={columns}
        data={data}
        isPagination={true}
        paginationInfo={paginationInfo}
        onPageChange={setPage}
        loading={isLoading || isRefetching}
      />

      {/* Modal for Add/Edit */}
      <VehicleMovementAddEdit
        modal={modal}
        isEdit={isEdit}
        movement={movement}
        optionLoading={optionLoading}
        toggleModal={toggleModal}
        employeesData={employeesData}
        movementTypesData={movementTypesData}
        locationsData={locationsData}
        lockedVehicle={vehicle}
        lockedCustomer={customer}
      />
    </>
  )
}

export default VehicleMovementList
